import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Plus, Ruler, Trash2 } from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router";
import { ApiError, apiFetch, setCsrfToken } from "@contractor-quoting/api-client";
import type {
  MaterialIntent,
  ProjectSpace,
  QuantityResult,
} from "@contractor-quoting/shared-types";
import { Badge, Button, Card, Input, SecondaryButton } from "@contractor-quoting/shared-ui";
import { FeetInchesInput } from "../components/FeetInchesInput.js";
import { QuoteSidebar } from "../components/QuoteSidebar.js";
import { useProjectAutosave } from "../hooks/useProjectAutosave.js";
import { loadPendingProjectFormDraft } from "../project-form-draft.js";
import { Shell } from "../components/Shell.js";

interface Summary {
  quantities: QuantityResult;
  approximateLayoutSvg: string;
}

interface UploadedImage {
  id: string;
  filename: string;
}

interface ProjectDraftResponse {
  status: string;
  spaces: ProjectSpace[];
  materialIntent?: MaterialIntent;
}

interface QuoteInitialization {
  projectId: string;
  draft: ProjectDraftResponse | null;
  images: UploadedImage[];
  summary: Summary | null;
}

interface SavedEstimate {
  currency: string;
  total: { lowMinor: string; highMinor: string };
}

function newRoom(index: number): ProjectSpace {
  return {
    id: crypto.randomUUID(),
    name: index === 0 ? "Living room" : `Room ${index + 1}`,
    kind: "room",
    shape: "rectangle",
    rectangles: [
      {
        id: crypto.randomUUID(),
        label: "Main area",
        widthMm: 3658,
        lengthMm: 4267,
        offsetXMm: 0,
        offsetYMm: 0,
      },
    ],
    exclusions: [],
    removeExistingFlooring: false,
    requiresStairReview: false,
  };
}

export function QuotePage() {
  const { contractorSlug = "" } = useParams();
  const [searchParams] = useSearchParams();
  const jobTypeCode = searchParams.get("jobType") || "flooring";
  const initialization = useRef<{
    slug: string;
    promise: Promise<QuoteInitialization>;
  } | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [spaces, setSpaces] = useState<ProjectSpace[]>([newRoom(0)]);
  const [material, setMaterial] = useState<MaterialIntent>({
    family: "vinyl_plank",
    tier: "standard",
  });
  const [summary, setSummary] = useState<Summary | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [savedEstimate, setSavedEstimate] = useState<SavedEstimate | null>(null);
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!contractorSlug) return;
    const selectionKey = `${contractorSlug}:${jobTypeCode}`;
    if (initialization.current?.slug !== selectionKey) {
      initialization.current = {
        slug: selectionKey,
        promise: (async () => {
          const session = await apiFetch<{
            projectId: string;
            csrfToken: string;
            resumed?: boolean;
          }>("/v1/quote/sessions", {
            method: "POST",
            body: JSON.stringify({ contractorSlug, jobTypeCode }),
          });
          setCsrfToken(session.csrfToken);
          if (!session.resumed) {
            return { projectId: session.projectId, draft: null, images: [], summary: null };
          }
          const [draft, imageResult] = await Promise.all([
            apiFetch<ProjectDraftResponse>("/v1/quote/project"),
            apiFetch<{ images: UploadedImage[] }>("/v1/quote/images"),
          ]);
          const summary =
            draft.status === "ready_for_estimate"
              ? await apiFetch<Summary>("/v1/quote/summary")
              : null;
          return { projectId: session.projectId, draft, images: imageResult.images, summary };
        })(),
      };
    }
    const current = initialization.current;
    let active = true;
    void current.promise
      .then((result) => {
        if (!active) return;
        const pending = loadPendingProjectFormDraft(result.projectId);
        setProjectId(result.projectId);
        if (pending) {
          setSpaces(pending.spaces);
          setMaterial(pending.materialIntent);
        } else {
          if (result.draft?.spaces.length) setSpaces(result.draft.spaces);
          if (result.draft?.materialIntent) setMaterial(result.draft.materialIntent);
        }
        setImages(result.images);
        setSummary(result.summary);
        setConfirmed(result.draft?.status === "ready_for_estimate");
        setHydrated(true);
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : "Could not start project");
      });
    return () => {
      active = false;
    };
  }, [contractorSlug, jobTypeCode]);

  const autosave = useProjectAutosave({
    projectId,
    ready: hydrated && !confirmed,
    spaces,
    materialIntent: material,
  });

  const updateSpace = (spaceId: string, update: (space: ProjectSpace) => ProjectSpace) => {
    setSpaces((current) => current.map((space) => (space.id === spaceId ? update(space) : space)));
    setSummary(null);
    setConfirmed(false);
  };

  const updateRectangle = (
    spaceId: string,
    rectangleId: string,
    field: "widthMm" | "lengthMm",
    value: number,
  ) => {
    updateSpace(spaceId, (space) => ({
      ...space,
      rectangles: space.rectangles.map((rectangle) =>
        rectangle.id === rectangleId ? { ...rectangle, [field]: value } : rectangle,
      ),
    }));
  };

  const toggleLShape = (spaceId: string) => {
    updateSpace(spaceId, (space) =>
      space.shape === "l_shape"
        ? { ...space, shape: "rectangle", rectangles: space.rectangles.slice(0, 1) }
        : {
            ...space,
            shape: "l_shape",
            rectangles: [
              ...space.rectangles,
              {
                id: crypto.randomUUID(),
                label: "L-shape extension",
                widthMm: 1829,
                lengthMm: 1829,
                offsetXMm: 3658,
                offsetYMm: 0,
              },
            ],
          },
    );
  };

  const toggleCabinetExclusion = (spaceId: string) => {
    updateSpace(spaceId, (space) => ({
      ...space,
      exclusions:
        space.exclusions.length > 0
          ? []
          : [
              {
                id: crypto.randomUUID(),
                label: "Permanent cabinets",
                widthMm: 610,
                lengthMm: 1829,
                offsetXMm: 0,
                offsetYMm: 0,
              },
            ],
    }));
  };

  const saveAndReview = async () => {
    try {
      setConfirmed(false);
      await apiFetch("/v1/quote/project", {
        method: "PUT",
        body: JSON.stringify({ spaces, transitions: [], materialIntent: material, answers: {} }),
      });
      setSummary(await apiFetch<Summary>("/v1/quote/summary"));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not calculate project");
    }
  };

  const confirm = async () => {
    try {
      setError("");
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const review = await apiFetch<{
          reviewId: string | null;
          hardBlockers: Array<{ message: string }>;
        }>("/v3/quote/reviews", { method: "POST" });
        if (!review.reviewId || review.hardBlockers.length > 0) {
          setError(
            review.hardBlockers[0]?.message ?? "Review the project details before confirming.",
          );
          return;
        }
        try {
          const estimate = await apiFetch<SavedEstimate>(
            `/v3/quote/reviews/${review.reviewId}/estimate`,
            {
              method: "POST",
              body: JSON.stringify({ acknowledgedAccuracyGapIds: [] }),
            },
          );
          setSavedEstimate(estimate);
          setConfirmed(true);
          return;
        } catch (reason) {
          if (!(reason instanceof ApiError) || reason.code !== "stale_review" || attempt === 1) {
            throw reason;
          }
        }
      }
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "The project could not be confirmed. Your measurements are still saved.",
      );
    }
  };

  const uploadImage = async (file: File) => {
    try {
      const result = await apiFetch<{
        imageId: string;
        upload: { url: string; fields: Record<string, string> };
      }>("/v1/quote/images/upload-policy", {
        method: "POST",
        body: JSON.stringify({ filename: file.name, contentType: file.type, sizeBytes: file.size }),
      });
      if (!result.upload.url.includes("/v1/local-uploads/")) {
        const form = new FormData();
        for (const [key, value] of Object.entries(result.upload.fields)) form.append(key, value);
        form.append("file", file);
        const response = await fetch(result.upload.url, { method: "POST", body: form });
        if (!response.ok) throw new Error("The private image upload failed");
      }
      await apiFetch(`/v1/quote/images/${result.imageId}/complete`, { method: "POST" });
      setImages((current) => [...current, { id: result.imageId, filename: file.name }]);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not upload the image");
    }
  };

  const area = useMemo(() => summary?.quantities.requiredMaterial.squareFeet, [summary]);
  const progress = confirmed ? 4 : summary ? 3 : images.length > 0 ? 2 : projectId ? 1 : 0;

  if (!hydrated) {
    return (
      <Shell>
        <main className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <Card className="mx-auto max-w-xl text-center">
            <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
              Flooring project
            </p>
            <h1 className="mt-3 text-2xl font-bold text-emerald-950">Resuming your project…</h1>
            <p className="mt-3 text-stone-600">
              Restoring your measurements, materials, and reference photos.
            </p>
            {error ? <p className="mt-5 rounded-xl bg-red-50 p-4 text-red-800">{error}</p> : null}
          </Card>
        </main>
      </Shell>
    );
  }

  return (
    <Shell>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
              Flooring project
            </p>
            <h1 className="text-3xl font-black">Measure, review, then save</h1>
          </div>
          <div className="text-right">
            <Badge>
              {confirmed
                ? "Confirmed"
                : summary
                  ? "Ready to confirm"
                  : projectId
                    ? "Draft in progress"
                    : "Starting…"}
            </Badge>
            {hydrated && !confirmed ? (
              <p className="mt-1 text-xs font-medium text-stone-500" aria-live="polite">
                {autosave.status === "saving"
                  ? "Saving privately…"
                  : autosave.status === "error"
                    ? "Autosave paused"
                    : "Saved privately in this browser"}
              </p>
            ) : null}
          </div>
        </div>
        <div
          className="mb-7 grid grid-cols-4 gap-2"
          aria-label={`Project progress: ${progress} of 4`}
        >
          {["Measure", "Materials", "Review", "Confirm"].map((step, index) => (
            <div key={step}>
              <div
                className={`h-2 rounded-full ${index < progress ? "bg-emerald-700" : "bg-stone-200"}`}
              />
              <p className="mt-1 text-xs font-semibold text-stone-600">{step}</p>
            </div>
          ))}
        </div>
        {error ? <p className="mb-5 rounded-xl bg-red-50 p-4 text-red-800">{error}</p> : null}
        {autosave.error ? (
          <p className="mb-5 rounded-xl bg-red-50 p-4 text-red-800">{autosave.error}</p>
        ) : null}
        <div className="grid items-start gap-6 lg:grid-cols-[1fr_380px]">
          <div className="grid gap-6">
            {spaces.map((space, spaceIndex) => (
              <Card key={space.id}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Ruler className="text-emerald-800" />
                    <h2 className="text-xl font-bold">
                      {space.kind === "closet" ? "Closet" : "Room"} measurements
                    </h2>
                  </div>
                  {spaces.length > 1 ? (
                    <button
                      aria-label={`Remove ${space.name}`}
                      onClick={() =>
                        setSpaces((current) => current.filter((item) => item.id !== space.id))
                      }
                    >
                      <Trash2 size={19} />
                    </button>
                  ) : null}
                </div>
                <label className="mt-5 block font-semibold">
                  Space name
                  <Input
                    value={space.name}
                    onChange={(event) =>
                      updateSpace(space.id, (current) => ({ ...current, name: event.target.value }))
                    }
                  />
                </label>
                {space.rectangles.map((rectangle, rectangleIndex) => (
                  <div className="mt-5" key={rectangle.id}>
                    <p className="mb-2 text-sm font-bold text-stone-600">
                      {rectangleIndex === 0 ? "Main area" : "L-shape extension"}
                    </p>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <FeetInchesInput
                        label="Width"
                        millimetres={rectangle.widthMm}
                        onChange={(value) =>
                          updateRectangle(space.id, rectangle.id, "widthMm", value)
                        }
                      />
                      <FeetInchesInput
                        label="Length"
                        millimetres={rectangle.lengthMm}
                        onChange={(value) =>
                          updateRectangle(space.id, rectangle.id, "lengthMm", value)
                        }
                      />
                    </div>
                  </div>
                ))}
                <div className="mt-5 flex flex-wrap gap-4 text-sm font-semibold">
                  <label>
                    <input
                      type="checkbox"
                      checked={space.shape === "l_shape"}
                      onChange={() => toggleLShape(space.id)}
                    />{" "}
                    L-shaped space
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={space.exclusions.length > 0}
                      onChange={() => toggleCabinetExclusion(space.id)}
                    />{" "}
                    Exclude permanent cabinets
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={space.removeExistingFlooring}
                      onChange={(event) =>
                        updateSpace(space.id, (current) => ({
                          ...current,
                          removeExistingFlooring: event.target.checked,
                        }))
                      }
                    />{" "}
                    Remove existing flooring
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={space.requiresStairReview}
                      onChange={(event) =>
                        updateSpace(space.id, (current) => ({
                          ...current,
                          requiresStairReview: event.target.checked,
                        }))
                      }
                    />{" "}
                    Includes stairs
                  </label>
                </div>
                {spaceIndex === spaces.length - 1 ? (
                  <div className="mt-5 flex flex-wrap gap-3">
                    <SecondaryButton
                      onClick={() => setSpaces((current) => [...current, newRoom(current.length)])}
                    >
                      <Plus size={18} /> Add room
                    </SecondaryButton>
                    <SecondaryButton
                      onClick={() =>
                        setSpaces((current) => [
                          ...current,
                          { ...newRoom(current.length), kind: "closet", name: "Closet" },
                        ])
                      }
                    >
                      <Plus size={18} /> Add closet
                    </SecondaryButton>
                  </div>
                ) : null}
              </Card>
            ))}
            <Card>
              <h2 className="text-xl font-bold">Material intent</h2>
              <p className="mt-1 text-stone-600">
                Choose the general family and quality level—not a specific product.
              </p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <label className="font-semibold">
                  Family
                  <select
                    className="mt-1 min-h-12 w-full rounded-xl border border-stone-300 bg-white px-4"
                    value={material.family}
                    onChange={(event) => {
                      setMaterial((current) => ({
                        ...current,
                        family: event.target.value as MaterialIntent["family"],
                      }));
                      setSummary(null);
                      setConfirmed(false);
                    }}
                  >
                    {[
                      "sheet_vinyl",
                      "vinyl_plank",
                      "laminate",
                      "engineered_hardwood",
                      "solid_hardwood",
                      "tile",
                      "other",
                      "unsure",
                    ].map((value) => (
                      <option key={value} value={value}>
                        {value.replaceAll("_", " ")}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="font-semibold">
                  Market tier
                  <select
                    className="mt-1 min-h-12 w-full rounded-xl border border-stone-300 bg-white px-4"
                    value={material.tier}
                    onChange={(event) => {
                      setMaterial((current) => ({
                        ...current,
                        tier: event.target.value as MaterialIntent["tier"],
                      }));
                      setSummary(null);
                      setConfirmed(false);
                    }}
                  >
                    {["economy", "standard", "mid_market", "premium", "luxury"].map((value) => (
                      <option key={value} value={value}>
                        {value.replaceAll("_", " ")}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <Button className="mt-6" onClick={() => void saveAndReview()}>
                Calculate project summary
              </Button>
            </Card>
            {summary ? (
              <Card>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="text-emerald-700" />
                  <h2 className="text-xl font-bold">Project summary</h2>
                </div>
                <p className="mt-4 text-4xl font-black text-emerald-950">{area} sq ft</p>
                <p className="text-stone-600">
                  Required planning material, including separate measurement and waste allowances.
                </p>
                <img
                  className="mt-5 w-full rounded-xl border border-stone-200"
                  alt="Approximate room layout"
                  src={`data:image/svg+xml,${encodeURIComponent(summary.approximateLayoutSvg)}`}
                />
                <p className="mt-2 text-sm font-semibold text-stone-600">Approximate room layout</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  {savedEstimate ? (
                    <>
                      <Badge>Planning estimate saved</Badge>
                      <p className="w-full text-lg font-black text-emerald-950">
                        {new Intl.NumberFormat("en-CA", {
                          style: "currency",
                          currency: savedEstimate.currency,
                        }).format(Number(savedEstimate.total.lowMinor) / 100)}
                        {savedEstimate.total.lowMinor === savedEstimate.total.highMinor
                          ? ""
                          : ` – ${new Intl.NumberFormat("en-CA", {
                              style: "currency",
                              currency: savedEstimate.currency,
                            }).format(Number(savedEstimate.total.highMinor) / 100)}`}
                      </p>
                      <p className="w-full text-sm text-stone-600">
                        Draft quote · Awaiting contractor approval
                      </p>
                      <SecondaryButton>
                        <Link to="/login?claim=true">Sign in to save & submit</Link>
                      </SecondaryButton>
                    </>
                  ) : confirmed ? (
                    <Badge>Measurements confirmed</Badge>
                  ) : (
                    <Button onClick={() => void confirm()}>Confirm & save estimate</Button>
                  )}
                </div>
              </Card>
            ) : null}
          </div>
          <QuoteSidebar
            contractorSlug={contractorSlug}
            images={images}
            onUpload={(file) => void uploadImage(file)}
          />
        </div>
      </main>
    </Shell>
  );
}
